import React, { ForwardedRef, forwardRef, useEffect } from 'react';
import {
  OverlayTriggerState,
  Popover as AriaPopover,
  PopoverProps as RACPopoverProps,
  OverlayTriggerStateContext,
} from 'react-aria-components';
import {
  SapphireStyleProps,
  useSapphireStyleProps,
} from '../../utils/useSapphireStyleProps';
import clsx from 'clsx';
import popoverStyles from '@danske/sapphire-css/components/popover/popover.module.css';
import { AriaPositionPropsKeys, GlobalDomAttributes } from '../../utils/types';
import { FocusScope } from 'react-aria/FocusScope';
import { ariaHideOutside } from 'react-aria/private/overlays/ariaHideOutside';
import { useObjectRef } from 'react-aria/useObjectRef';

export interface PopoverProps
  extends Pick<
      RACPopoverProps,
      | 'triggerRef'
      | 'isNonModal'
      | 'isKeyboardDismissDisabled'
      | 'shouldCloseOnInteractOutside'
      | AriaPositionPropsKeys
    >,
    SapphireStyleProps,
    GlobalDomAttributes {
  state?: OverlayTriggerState;
  children: React.ReactNode;
  /**
   * Whether to include the default popover padding.
   * @default false
   */
  padded?: boolean;
  /**
   * Whether it has a predefined size constraint. Useful when the popover is
   * used as a dropdown of selects, menus etc.
   * @default false
   */
  defaultMaxWidth?: boolean;

  restoreFocus?: boolean;
}

/**
 * Helper component intended for internal use within Sapphire in components
 * like Select and Menu.
 *
 * *Popovers* are containers used to display transient content such as menus,
 * options, additional actions etc.
 *
 * @internal
 */
export const Popover = forwardRef(function Popover(
  {
    children,
    padded,
    defaultMaxWidth,
    maxHeight,
    state,
    restoreFocus = false,
    ...props
  }: PopoverProps,
  forwardedRef: ForwardedRef<HTMLDivElement>
) {
  const ref = useObjectRef(forwardedRef);
  const { styleProps } = useSapphireStyleProps({ ...props, maxHeight });

  /*
   * We need this effect to fix an issue in react-aria's "usePopover". The
   * issue is that a popover which is not a modal (see prop "isNonModal") will
   * get aria-hidden if opened in an existing modal. See more here
   * https://github.com/adobe/react-spectrum/issues/4213
   *
   * This same hack is present in react-aria's useComboBox.
   * https://github.com/adobe/react-spectrum/blob/main/packages/%40react-aria/combobox/src/useComboBox.ts#L300-L304
   */
  useEffect(() => {
    if (state && state.isOpen && props.isNonModal) {
      // input to ariaHideOutside is what NOT to hide (and their ancestors).
      return ariaHideOutside([
        props.triggerRef?.current as Element,
        ref?.current as Element,
      ]);
    }
  }, [state?.isOpen, props.isNonModal, ref, props.triggerRef]);

  const popover = (
    <AriaPopover
      {...props}
      ref={ref}
      style={styleProps.style}
      className={clsx(styleProps.className, popoverStyles['sapphire-popover'], {
        [popoverStyles['sapphire-popover--padded']]: padded,
        [popoverStyles['sapphire-popover--max-width']]: defaultMaxWidth,
      })}
    >
      <div
        className={popoverStyles['sapphire-popover__content']}
        role="presentation"
      >
        {/*
         * TODO UC-5016
         * The Overlay component (from react-aria-components) already wraps the
         * portal content in a FocusScope with restoreFocus and contain.
         * We only need autoFocus here to focus the first focusable child
         * (rather than the popover container itself, which is what RAC does by default).
         */}
        <FocusScope
          autoFocus={!props.isNonModal}
          contain={!props.isNonModal}
          restoreFocus={restoreFocus}
        >
          {children}
        </FocusScope>
      </div>
    </AriaPopover>
  );

  if (state) {
    return (
      <OverlayTriggerStateContext.Provider value={state}>
        {popover}
      </OverlayTriggerStateContext.Provider>
    );
  }

  return popover;
});
